import React, { useState, useEffect, useMemo } from "react";
import { useGetClarificationQuery } from "../../redux/services/apiSlice";
import Loader from "../../components/Loader";
import { convertDateFormat } from "../../Utils/utils";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { useSelector } from "react-redux";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import usePagination from "../../hooks/usePagination";
import Pagination from "../../components/Pagination/Pagination";
import TenderSearch from "../../components/TenderSearch";
import { useTableResponsive } from "../../hooks/useTableResponsive";
import useSeo from "../../hooks/useSeo";
import { useLocation } from "react-router-dom";
import PdfZipItem from "../../components/PdfZipItem";

const PreBidClarifications = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredTenders, setFilteredTenders] = useState([]);
  const { languageType } = useSelector((state) => state.homeSlice);
  const { getText } = useLanguageTranslation();

  const {
    data: queriesData = [],
    isLoading: queriesIsLoading,
    isFetching: queriesIsFetching,
  } = useGetClarificationQuery({ lang: languageType });

  const lastUpdatedOn = useMemo(() => {
    return convertDateFormat(queriesData?.tender_data?.[0]?.last_update_date);
  }, [queriesData]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = queriesData.tender_data.filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredTenders(filtered);
    } else {
      setFilteredTenders(queriesData.tender_data);
    }
  }, [searchTerm, queriesData]);

  const { currentItems, currentPage, totalPages, handlePageChange } =
    usePagination(filteredTenders, 12);

  useTableResponsive(queriesData);

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerImage, innerBannerTitle } = useSeo({ pathname });

  if (queriesIsLoading || queriesIsFetching) {
    return <Loader />;
  }

  return (
    <>
      <InnerBannerBreadcrumb
        title={getText("Tender", "निविदा")}
        titleLink="/tender/queries-posted-by-bidders"
        levelTwo={true}
        levelThree={false}
        bannerImage={innerBannerImage}
        titleTwo={innerBannerTitle}
        isActive={innerBannerTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 col-lg-6 mb-3 d-flex justify-content-between">
                <TenderSearch
                  setSearchTerm={setSearchTerm}
                  title={"Tender Number"}
                />
              </div>
              {/* <div className="col-12 col-md-6 col-lg-6 mb-3 d-flex justify-content-end">
                <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
              </div> */}
            </div>
            <div className="row">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                <div className="table-responsive">
                  <table className="table table-striped custom-table">
                    <thead className="thead-dark">
                      <tr>
                        <th>{getText("Tender Number", "निविदा संख्या")}</th>
                        <th>{getText("Tender Date", "निविदा दिनांक")}</th>
                        <th>{getText("Tender Description", "निविदा विवरण")}</th>
                        <th className="no-wrap-td">
                          {getText("Clarification", "स्पष्टीकरण")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems?.length > 0 ? (
                        currentItems?.map((tender, index) => (
                          <tr key={index}>
                            <td
                              className="no-wrap-td"
                              data-key={getText(
                                "Tender Number",
                                "निविदा संख्या",
                              )}
                            >
                              <PdfZipItem
                                filePath={tender?.uri}
                                title={tender.title}
                                filesize={tender.file_size}
                              />
                            </td>
                            <td
                              className="no-wrap-td"
                              data-key={getText("Tender Date", "निविदा दिनांक")}
                            >
                              {convertDateFormat(
                                tender.field_tender_date_value,
                              )}
                            </td>
                            <td
                              data-key={("Tender Description", "निविदा विवरण")}
                              dangerouslySetInnerHTML={{
                                __html:
                                  tender.field_short_notice_inviting_bids_value,
                              }}
                            ></td>
                            <td
                              className="no-wrap-td"
                              data-key={getText("Clarification", "स्पष्टीकरण")}
                            >
                              {tender?.clarification?.files?.map(
                                (subitem, i) => (
                                  <PdfZipItem
                                    key={i}
                                    filePath={subitem?.clarification_uri}
                                    title={subitem.file_description}
                                    filesize={subitem.file_size}
                                  />
                                ),
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
            <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
          </div>
        </div>
      </div>
    </>
  );
};

export default PreBidClarifications;
